import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { z } from "zod";
import { AlertTriangle, Camera, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { business } from "@/config/business";
import { useI18n } from "@/i18n";
import { intakeCopy } from "@/i18n/intake-copy";
import { localizedPath } from "@/i18n/route-manifest";
import { initializeAnalytics, trackAnalyticsEvent } from "@/lib/analytics";
import {
  captureAttribution,
  isLeadTransportConfigured,
  LeadTransportUnavailableError,
  submitLead,
} from "@/lib/leads";
import { photoFollowUpNotice, selectedPhotos, structuredMessage } from "@/lib/intake";
import { IntakeField, IntakeSelect, type IntakeStatus } from "./intake-form-shared";

export function EmergencyIntakeForm() {
  const { locale } = useI18n();
  const copy = intakeCopy(locale);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [photos, setPhotos] = useState<File[]>([]);
  const [status, setStatus] = useState<IntakeStatus>(
    isLeadTransportConfigured() ? "idle" : "unavailable",
  );
  const started = useRef(false);

  useEffect(() => {
    initializeAnalytics();
    captureAttribution();
  }, []);

  function trackStart() {
    if (started.current) return;
    started.current = true;
    trackAnalyticsEvent({
      event: "estimate_started",
      form_id: "emergency_intake",
      lead_type: "emergency",
    });
  }

  function onPhotos(event: ChangeEvent<HTMLInputElement>) {
    const next = selectedPhotos(event);
    setPhotos(next);
    if (next.length) {
      trackAnalyticsEvent({
        event: "photo_uploaded",
        form_id: "emergency_intake",
        lead_type: "emergency",
        file_count: String(next.length),
        file_status: "selected_not_transmitted",
      });
    }
  }

  const schema = z.object({
    name: z.string().trim().min(1, copy.emergency.errors.name).max(100),
    phone: z
      .string()
      .trim()
      .min(7, copy.emergency.errors.phone)
      .max(30)
      .regex(/^[0-9+()\-.\s]+$/, copy.emergency.errors.phone),
    email: z.string().trim().email(copy.emergency.errors.email).max(255),
    location: z.string().trim().min(3, copy.emergency.errors.location).max(200),
    city: z.string().trim().min(1, copy.emergency.errors.city).max(100),
    description: z.string().trim().min(5, copy.emergency.errors.description).max(1500),
    waterEntry: z.string().min(1, copy.emergency.errors.option),
    roofType: z.string().min(1, copy.emergency.errors.option),
  });

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const parsed = schema.safeParse(Object.fromEntries(new FormData(form).entries()));
    if (!parsed.success) {
      const next: Record<string, string> = {};
      for (const issue of parsed.error.issues) next[String(issue.path[0])] ??= issue.message;
      setErrors(next);
      form.querySelector<HTMLElement>(`[name="${Object.keys(next)[0]}"]`)?.focus();
      return;
    }

    setErrors({});
    setStatus("sending");
    const data = parsed.data;
    try {
      await submitLead({
        name: data.name,
        phone: data.phone,
        email: data.email,
        postalCode: data.location,
        roofingNeed: `lead_type=emergency | ${data.roofType} | water_entering=${data.waterEntry}`,
        timeline: "Emergency / urgent",
        message: structuredMessage(
          "lead_type=emergency",
          [
            ["Address or postal code", data.location],
            ["City", data.city],
            ["Water entering building", data.waterEntry],
            ["Roof type", data.roofType],
          ],
          data.description,
          photos.length,
        ),
        locale,
      });
      trackAnalyticsEvent({
        event: "estimate_completed",
        form_id: "emergency_intake",
        lead_type: "emergency",
      });
      form.reset();
      setPhotos([]);
      window.location.assign(localizedPath("core:thank-you", locale));
    } catch (error) {
      setStatus(error instanceof LeadTransportUnavailableError ? "unavailable" : "error");
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      onFocusCapture={trackStart}
      onChange={trackStart}
      noValidate
      className="rounded-xl border border-border bg-card p-6 shadow-card sm:p-8"
    >
      <div className="mb-6 flex items-start gap-3 rounded-lg border border-gold/45 bg-gold/10 p-4">
        <AlertTriangle className="mt-0.5 size-5 shrink-0 text-accent" aria-hidden="true" />
        <p className="text-sm leading-6">
          {copy.emergency.alertBefore}{" "}
          <a className="font-semibold underline underline-offset-2" href={business.phoneHref}>
            {business.phone}
          </a>
          . {copy.emergency.alertAfter}
        </p>
      </div>
      <div className="grid gap-5 sm:grid-cols-2">
        <IntakeField id="emergency-name" label={copy.emergency.name} error={errors["name"]}>
          <Input
            id="emergency-name"
            name="name"
            autoComplete="name"
            required
            aria-invalid={!!errors["name"]}
            aria-describedby={errors["name"] ? "emergency-name-error" : undefined}
          />
        </IntakeField>
        <IntakeField id="emergency-phone" label={copy.emergency.phone} error={errors["phone"]}>
          <Input
            id="emergency-phone"
            name="phone"
            type="tel"
            autoComplete="tel"
            required
            aria-invalid={!!errors["phone"]}
            aria-describedby={errors["phone"] ? "emergency-phone-error" : undefined}
          />
        </IntakeField>
        <IntakeField id="emergency-email" label={copy.emergency.email} error={errors["email"]}>
          <Input
            id="emergency-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            aria-invalid={!!errors["email"]}
            aria-describedby={errors["email"] ? "emergency-email-error" : undefined}
          />
        </IntakeField>
        <IntakeField
          id="emergency-location"
          label={copy.emergency.location}
          error={errors["location"]}
        >
          <Input
            id="emergency-location"
            name="location"
            autoComplete="street-address"
            required
            aria-invalid={!!errors["location"]}
            aria-describedby={errors["location"] ? "emergency-location-error" : undefined}
          />
        </IntakeField>
        <IntakeField id="emergency-city" label={copy.emergency.city} error={errors["city"]}>
          <Input
            id="emergency-city"
            name="city"
            autoComplete="address-level2"
            required
            aria-invalid={!!errors["city"]}
            aria-describedby={errors["city"] ? "emergency-city-error" : undefined}
          />
        </IntakeField>
        <IntakeField id="waterEntry" label={copy.emergency.waterEntry} error={errors["waterEntry"]}>
          <IntakeSelect
            id="waterEntry"
            options={copy.emergency.yesNo}
            error={errors["waterEntry"]}
          />
        </IntakeField>
        <IntakeField id="roofType" label={copy.emergency.roofType} error={errors["roofType"]}>
          <IntakeSelect
            id="roofType"
            options={copy.emergency.roofTypes}
            error={errors["roofType"]}
          />
        </IntakeField>
        <IntakeField id="emergency-photos" label={copy.emergency.photos} className="sm:col-span-2">
          <Input
            id="emergency-photos"
            name="photos"
            type="file"
            accept="image/*"
            multiple
            onChange={onPhotos}
          />
          <p className="mt-2 flex items-start gap-2 text-xs leading-5 text-muted-foreground">
            <Camera className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            {photoFollowUpNotice(photos.length, {
              empty: copy.common.photoEmpty,
              selected: copy.common.photoSelected,
            })}
          </p>
        </IntakeField>
        <IntakeField
          id="emergency-description"
          label={copy.emergency.description}
          error={errors["description"]}
          className="sm:col-span-2"
        >
          <Textarea
            id="emergency-description"
            name="description"
            rows={4}
            required
            aria-invalid={!!errors["description"]}
            aria-describedby={errors["description"] ? "emergency-description-error" : undefined}
          />
        </IntakeField>
      </div>
      {status === "error" || status === "unavailable" ? (
        <p role="alert" className="mt-4 text-sm text-destructive">
          {status === "unavailable" ? copy.common.unavailable : copy.common.sendError}
        </p>
      ) : null}
      <Button
        type="submit"
        variant="brand"
        size="xl"
        className="mt-6 w-full"
        disabled={status === "sending" || status === "unavailable"}
      >
        {status === "sending" ? copy.common.sending : copy.emergency.submit}
        <Phone aria-hidden="true" />
      </Button>
    </form>
  );
}
